import { Eye, Loader2 } from 'lucide-react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type {
	CommitmentMatrixDashboardCollaborator,
	CommitmentMatrixDashboardSortColumn,
} from '@/types'
import { formatPercentage } from '@/utils/format-percentage'
import {
	pillClassForPct,
	STATUS_LABEL,
	STATUS_PILL_CLASS,
} from './collaborator-status'
import { SortableHead, type SortState } from './sortable-head'

interface CollaboratorsTableProps {
	items: CommitmentMatrixDashboardCollaborator[]
	isFetching: boolean
	sort: SortState<CommitmentMatrixDashboardSortColumn>
	onSort: (column: CommitmentMatrixDashboardSortColumn) => void
	onSelectPerson: (personId: string) => void
}

export function CollaboratorsTable({
	items,
	isFetching,
	sort,
	onSort,
	onSelectPerson,
}: CollaboratorsTableProps) {
	return (
		<>
			{/* Collaborators — mobile cards */}
			<div className="relative sm:hidden">
				{isFetching && (
					<div className="absolute inset-0 z-10 flex items-start justify-center bg-white/60 pt-10">
						<Loader2 className="size-5 animate-spin text-kaefer-medium-grey" />
					</div>
				)}
				<div
					className={cn(
						'flex flex-col gap-2',
						isFetching && 'pointer-events-none opacity-50',
					)}
				>
					{items.length === 0 ? (
						<p className="rounded-lg border border-kaefer-light-silver bg-white py-10 text-center text-sm text-muted-foreground">
							Nenhum colaborador encontrado para os filtros selecionados.
						</p>
					) : (
						items.map((c) => (
							<CollaboratorCard
								key={c.personId}
								collaborator={c}
								onClick={() => onSelectPerson(c.personId)}
							/>
						))
					)}
				</div>
			</div>

			{/* Collaborators table */}
			<div className="relative hidden rounded-lg border border-kaefer-light-silver overflow-hidden sm:block">
				{isFetching && (
					<div className="absolute inset-0 z-10 flex items-start justify-center bg-white/60 pt-10">
						<Loader2 className="size-5 animate-spin text-kaefer-medium-grey" />
					</div>
				)}
				<Table className={cn(isFetching && 'pointer-events-none opacity-50')}>
					<TableHeader>
						<TableRow className="bg-muted/30">
							<SortableHead column="registration" sort={sort} onSort={onSort}>
								Matrícula
							</SortableHead>
							<SortableHead column="name" sort={sort} onSort={onSort}>
								Nome
							</SortableHead>
							<SortableHead column="jobRole" sort={sort} onSort={onSort}>
								Cargo
							</SortableHead>
							<TableHead>Grupo</TableHead>
							<SortableHead column="branchName" sort={sort} onSort={onSort}>
								Filial
							</SortableHead>
							<SortableHead
								column="target"
								sort={sort}
								onSort={onSort}
								align="right"
								tooltip="Meta de inspeções no período"
							>
								Meta
							</SortableHead>
							<SortableHead
								column="totalCount"
								sort={sort}
								onSort={onSort}
								align="right"
								tooltip="Todas as inspeções do período, com Cartão Amarelo"
							>
								Total
							</SortableHead>
							<SortableHead
								column="vereAgirCount"
								sort={sort}
								onSort={onSort}
								align="right"
								tooltip="Ver e Agir"
							>
								VEA
							</SortableHead>
							<SortableHead
								column="direcionadaCount"
								sort={sort}
								onSort={onSort}
								align="right"
								tooltip="Direcionada"
							>
								DIR
							</SortableHead>
							<SortableHead
								column="direcionadaCompliancePct"
								sort={sort}
								onSort={onSort}
								align="right"
								tooltip="% conforme na Direcionada"
							>
								<span className="inline-flex items-center gap-1">
									<Eye className="size-3.5" />% conf.
								</span>
							</SortableHead>
							<SortableHead
								column="interacaoCount"
								sort={sort}
								onSort={onSort}
								align="right"
								tooltip="Interação de Segurança"
							>
								INT
							</SortableHead>
							<SortableHead
								column="cartaoAmareloCount"
								sort={sort}
								onSort={onSort}
								align="right"
								tooltip="Cartão Amarelo"
							>
								CA
							</SortableHead>
							<SortableHead
								column="adherencePct"
								sort={sort}
								onSort={onSort}
								align="right"
								tooltip="Aderência à meta (Cartão Amarelo não conta)"
							>
								%
							</SortableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{items.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={13}
									className="py-10 text-center text-sm text-muted-foreground whitespace-normal"
								>
									Nenhum colaborador encontrado para os filtros selecionados.
								</TableCell>
							</TableRow>
						) : (
							items.map((c) => (
								<Tooltip key={c.personId}>
									<TooltipTrigger asChild>
										<TableRow
											className="cursor-pointer hover:bg-muted/40"
											onClick={() => onSelectPerson(c.personId)}
										>
											<TableCell className="text-muted-foreground">
												{c.registration}
											</TableCell>
											<TableCell className="font-medium text-kaefer-dark-grey">
												{c.name}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{c.jobRole ?? '—'}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{c.clusterName ?? c.jobGroupName ?? '—'}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{c.branchName}
											</TableCell>
											<TableCell className="text-right">
												<TargetCell collaborator={c} />
											</TableCell>
											<TableCell className="text-right font-semibold text-kaefer-dark-grey">
												{c.totalCount}
											</TableCell>
											<TableCell className="text-right">
												{c.vereAgirCount}
											</TableCell>
											<TableCell className="text-right">
												{c.direcionadaCount}
											</TableCell>
											<TableCell className="text-right">
												<DirecionadaComplianceCell collaborator={c} />
											</TableCell>
											<TableCell className="text-right">
												{c.interacaoCount}
											</TableCell>
											<TableCell className="text-right">
												{c.cartaoAmareloCount}
											</TableCell>
											<TableCell className="text-right">
												<span
													className={cn(
														'inline-flex rounded-full px-2 py-0.5 text-xs font-semibold',
														STATUS_PILL_CLASS[c.status] ??
															pillClassForPct(c.adherencePct),
													)}
												>
													{formatPercentage(c.adherencePct)}
												</span>
											</TableCell>
										</TableRow>
									</TooltipTrigger>
									<TooltipContent
										side="top"
										align="center"
										className="max-w-[260px] text-left"
									>
										<CollaboratorTooltipContent collaborator={c} />
									</TooltipContent>
								</Tooltip>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</>
	)
}

function DirecionadaComplianceCell({
	collaborator: c,
}: {
	collaborator: CommitmentMatrixDashboardCollaborator
}) {
	if (c.direcionadaCompliancePct === null) {
		return <span className="text-muted-foreground">—</span>
	}

	return <>{formatPercentage(c.direcionadaCompliancePct)}</>
}

function TargetCell({
	collaborator: c,
}: {
	collaborator: CommitmentMatrixDashboardCollaborator
}) {
	if (c.target === c.originalTarget) return <>{c.target}</>

	return (
		<span className="inline-flex items-center justify-end gap-1.5">
			<span className="text-muted-foreground line-through">
				{c.originalTarget}
			</span>
			<span className="font-semibold text-kaefer-dark-grey">{c.target}</span>
		</span>
	)
}

function CollaboratorCard({
	collaborator: c,
	onClick,
}: {
	collaborator: CommitmentMatrixDashboardCollaborator
	onClick: () => void
}) {
	return (
		<div
			className="cursor-pointer rounded-lg border border-kaefer-light-silver bg-white p-3 active:bg-muted/40"
			onClick={onClick}
			onKeyDown={(e) => e.key === 'Enter' && onClick()}
			role="button"
			tabIndex={0}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0">
					<p className="truncate font-medium text-kaefer-dark-grey">{c.name}</p>
					<p className="text-xs text-muted-foreground">
						{c.registration} · {c.branchName}
					</p>
				</div>
				<span
					className={cn(
						'inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
						STATUS_PILL_CLASS[c.status] ?? pillClassForPct(c.adherencePct),
					)}
				>
					{formatPercentage(c.adherencePct)}
				</span>
			</div>

			<p className="mt-1 text-xs text-muted-foreground">
				{c.jobRole ?? '—'}
				{(c.clusterName ?? c.jobGroupName)
					? ` · ${c.clusterName ?? c.jobGroupName}`
					: ''}
			</p>

			<div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
				<span>
					{STATUS_LABEL[c.status]} ·{' '}
					{c.target !== c.originalTarget && (
						<span className="text-muted-foreground/70 line-through">
							{c.originalTarget}
						</span>
					)}{' '}
					{c.done}/{c.target}
				</span>
			</div>

			{c.direcionadaCompliancePct !== null && (
				<p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
					<Eye className="size-3.5" />
					Direcionada: {formatPercentage(c.direcionadaCompliancePct)} conforme
				</p>
			)}

			<div className="mt-2 grid grid-cols-4 gap-1 border-t border-kaefer-light-silver pt-2 text-center text-xs">
				<div>
					<p className="font-semibold text-kaefer-grass-green">
						{c.vereAgirCount}
					</p>
					<p className="text-muted-foreground">VEA</p>
				</div>
				<div>
					<p className="font-semibold text-kaefer-ocean-blue">
						{c.direcionadaCount}
					</p>
					<p className="text-muted-foreground">DIR</p>
				</div>
				<div>
					<p className="font-semibold text-kaefer-violet">{c.interacaoCount}</p>
					<p className="text-muted-foreground">INT</p>
				</div>
				<div>
					<p className="font-semibold text-kaefer-dark-grey">
						{c.cartaoAmareloCount}
					</p>
					<p className="text-muted-foreground">CA</p>
				</div>
			</div>
		</div>
	)
}

function CollaboratorTooltipContent({
	collaborator,
}: {
	collaborator: CommitmentMatrixDashboardCollaborator
}) {
	const c = collaborator
	return (
		<div className="flex flex-col gap-1">
			<p className="font-semibold text-kaefer-dark-grey">{c.name}</p>
			<p>
				<span className="text-muted-foreground">Matrícula:</span>{' '}
				{c.registration}
			</p>
			<p>
				<span className="text-muted-foreground">CR:</span> {c.crName}
			</p>
			<p>
				<span className="text-muted-foreground">Filial:</span> {c.branchName}
			</p>
			<p>
				<span className="text-muted-foreground">Cargo:</span> {c.jobRole ?? '—'}
			</p>
			<p>
				<span className="text-muted-foreground">Grupo:</span>{' '}
				{c.clusterName ?? c.jobGroupName ?? '—'}
			</p>
			<p>
				<span className="text-muted-foreground">Status:</span>{' '}
				{STATUS_LABEL[c.status]}
			</p>
			<p>
				<span className="text-muted-foreground">Progresso:</span> {c.done}/
				{c.target} ({formatPercentage(c.adherencePct)})
			</p>
			{c.target !== c.originalTarget && (
				<p>
					<span className="text-muted-foreground">Meta original:</span>{' '}
					{c.originalTarget} (ajustada por exceção)
				</p>
			)}
			<p>
				<span className="text-muted-foreground">Cartões amarelos:</span>{' '}
				{c.cartaoAmareloCount}
			</p>
			<p>
				<span className="text-muted-foreground">Direcionada conforme:</span>{' '}
				{c.direcionadaCompliancePct === null
					? '—'
					: `${formatPercentage(c.direcionadaCompliancePct)} (${c.direcionadaEvaluatedAnswers} respostas)`}
			</p>
		</div>
	)
}
